<template>
  <div class="fm-page-shell">
    <!-- ===== CONTEXTUAL RAIL ===== -->
    <aside class="fm-rail">
      <div class="fm-rail-label">Streams</div>
      <button type="button" class="fm-view" :class="{ 'fm-view--active': levelView === 'all' }" @click="levelView = 'all'">
        <span class="fm-view-dot" style="background:#94A3B8"></span>All
        <span class="fm-view-count">{{ traceLogs.length }}</span>
      </button>
      <button type="button" class="fm-view" :class="{ 'fm-view--active': levelView === 'errors' }" @click="levelView = 'errors'">
        <span class="fm-view-dot" style="background:#EF4444"></span>Errors
        <span class="fm-view-count">{{ errorCount }}</span>
      </button>
      <button type="button" class="fm-view" :class="{ 'fm-view--active': levelView === 'warnings' }" @click="levelView = 'warnings'">
        <span class="fm-view-dot" style="background:#F59E0B"></span>Warnings
        <span class="fm-view-count">{{ warningCount }}</span>
      </button>
    </aside>

    <!-- ===== MAIN ===== -->
    <main class="fm-main">
      <b-overlay :show="loading" spinner small>
        <div v-if="notAuthorized" class="fm-page">
          <div class="fm-empty">You are not authorized to view this page</div>
        </div>
        <div v-else class="fm-page">
          <div class="fm-page-head">
            <div class="fm-page-title">Logs</div>
            <button class="fm-btn fm-btn--ghost" @click="showFilters = !showFilters">
              <i class="fa-solid fa-sliders"></i>{{ showFilters ? 'Hide Filters' : 'Filters' }}
            </button>
          </div>
          <div class="fm-page-desc">Live gateway, agent and auth logs.</div>

          <div class="fm-toolbar">
            <div class="fm-search">
              <i class="fa-solid fa-magnifying-glass"></i>
              <b-form-input type="search" v-model="filters.search" placeholder="Search logs, services, agents..." @input="debouncedFetch"></b-form-input>
            </div>
            <div v-if="totalLogs > perPage" class="fm-toolbar-pager">
              <b-pagination
                v-model="currentPage"
                :total-rows="totalLogs"
                :per-page="perPage"
                size="sm"
                aria-controls="audit-table"
              />
            </div>
          </div>

          <!-- Filters Section -->
          <b-collapse v-model="showFilters" class="mb-3">
            <div class="fm-filters">
              <b-row>
                <b-col lg="3" md="6" class="mb-2">
                  <label class="fm-filter-label">User</label>
                  <b-form-input v-model="filters.user" placeholder="Enter username..." @input="debouncedFetch" />
                </b-col>
                <b-col lg="3" md="6" class="mb-2">
                  <label class="fm-filter-label">Service</label>
                  <b-form-input v-model="filters.service" placeholder="Enter service name..." @input="debouncedFetch" />
                </b-col>
                <b-col lg="3" md="6" class="mb-2">
                  <label class="fm-filter-label">Start Date</label>
                  <b-form-input v-model="filters.startDate" type="datetime-local" @input="fetchTraceLogs" />
                </b-col>
                <b-col lg="3" md="6" class="mb-2">
                  <label class="fm-filter-label">End Date</label>
                  <b-form-input v-model="filters.endDate" type="datetime-local" @input="fetchTraceLogs" />
                </b-col>
              </b-row>
              <div class="d-flex justify-content-between align-items-center mt-2">
                <button class="fm-btn fm-btn--danger" @click="clearFilters"><i class="fa-solid fa-xmark"></i>Clear All Filters</button>
                <small v-if="hasActiveFilters" class="text-muted">{{ activeFilterCount }} filter(s) active</small>
              </div>
            </div>
          </b-collapse>

          <div v-if="viewFilteredLogs.length > 0" class="fm-list">
            <div v-for="(log, idx) in viewFilteredLogs" :key="log.Timestamp + '-' + idx" class="fm-row" @click="openRow(log)">
              <div class="fm-row-main">
                <div class="fm-row-top">
                  <span class="fm-row-name">{{ logLevel(log) }}</span>
                  <i class="fa-regular fa-copy fm-row-copy"></i>
                  <span v-if="log.Service" class="fm-tag">{{ log.Service }}</span>
                </div>
                <div v-if="log.Data" class="fm-row-desc">{{ truncateLog(log.Data) }}</div>
              </div>
              <span class="fm-value-pill">{{ formatDateTime(log.Timestamp) }}</span>
              <div class="fm-status-cell">
                <span class="fm-status-pill" :class="'fm-status-pill--' + levelTone(logLevel(log))">
                  <span v-if="levelTone(logLevel(log)) !== 'neu'" class="fm-dot"></span>{{ logLevel(log) }}
                </span>
              </div>
              <i class="fa-solid fa-angle-right fm-chev"></i>
            </div>
          </div>
          <div v-else-if="traceLogs.length > 0" class="fm-empty">
            No logs match this filter.
          </div>
          <div v-else class="fm-empty">
            No logs found.
          </div>

          <!-- Full Log Modal -->
          <b-modal
            id="full-log-modal"
            v-model="showLogModal"
            title="Complete Log Entry"
            size="lg"
            scrollable
            hide-footer
            header-bg-variant="light"
          >
            <div class="full-log-content">
              <pre class="log-text">{{ selectedLog }}</pre>
            </div>
          </b-modal>
        </div>
      </b-overlay>
    </main>

    <!-- ===== DETAIL DRAWER ===== -->
    <detail-drawer
      :open="drawerOpen"
      kind="Log Line"
      :title="selected ? logLevel(selected) : ''"
      :status="selected ? logLevel(selected) : ''"
      :tone="selected ? levelTone(logLevel(selected)) : 'neu'"
      :fields="drawerFields"
      @close="closeDrawer">
      <template #footer>
        <button class="fm-btn fm-btn--primary" @click="drawerAction('full')">
          <i class="fa-solid fa-up-right-and-down-left-from-center"></i>View Full
        </button>
        <button class="fm-btn fm-btn--ghost" @click="drawerAction('copy')">
          <i class="fa-regular fa-copy"></i>Copy line
        </button>
      </template>
    </detail-drawer>
  </div>
</template>

<script>
import {BOverlay, BPagination, BModal, BRow, BCol, BFormInput, BCollapse} from "bootstrap-vue";
import DetailDrawer from '@/components/DetailDrawer.vue';
import apiClient from "@/http"; // Assuming you have a configured API client

export default {
  components: {
    DetailDrawer,
    BOverlay,
    BPagination,
    BModal,
    BRow,
    BCol,
    BFormInput,
    BCollapse,
  },
  data() {
    return {
      traceLogs: [],
      logFields: [
        {key: "Timestamp", label: "Date & Time", sortable: true},
        {key: "AgentCode", label: "Agent", sortable: true},
        {key: "Service", label: "Service", sortable: true},
        {key: "User", label: "User", sortable: true},
        {key: "Data", label: "Log", sortable: true},
      ],
      loading: false,
      currentPage: 1,
      perPage: 20,
      totalLogs: 0,
      sortBy: "Timestamp",
      sortDesc: true,
      notAuthorized: false,
      // Filter data properties
      filters: {
        user: "",
        service: "",
        startDate: "",
        endDate: "",
        search: "",
      },
      showFilters: false, // Collapsed by default
      // New properties for log modal
      showLogModal: false,
      selectedLog: "",
      logMaxLength: 100, // Maximum characters to show in the preview
      // Debounce timer
      debounceTimer: null,
      // Drawer + rail view state
      levelView: 'all',
      drawerOpen: false,
      selected: null,
    };
  },
  methods: {
    async fetchTraceLogs() {
      this.loading = true;
      try {
        const params = {
          page: this.currentPage,
          perPage: this.perPage,
          sortBy: this.sortBy,
          sortDesc: this.sortDesc,
        };

        // Add filters if they have values
        if (this.filters.user) params.userEmail = this.filters.user;
        if (this.filters.service) params.serviceName = this.filters.service;
        if (this.filters.startDate) params.startDate = this.filters.startDate;
        if (this.filters.endDate) params.endDate = this.filters.endDate;
        if (this.filters.search) params.search = this.filters.search;

        const response = await apiClient.get("/traces", { params });
        this.traceLogs = response.data.Logs;
        this.totalLogs = response.data.Total;
      } catch (error) {
        if (error.response && error.response.status === 403) {
          this.notAuthorized = true;
        }
        console.error("Failed to fetch trace logs:", error);
      } finally {
        this.loading = false;
      }
    },
    // Clear all filters
    clearFilters() {
      this.filters = {
        user: "",
        service: "",
        startDate: "",
        endDate: "",
        search: "",
      };
      this.currentPage = 1;
      this.fetchTraceLogs();
    },
    // Debounced fetch for search/filter inputs
    debouncedFetch() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      this.debounceTimer = setTimeout(() => {
        this.currentPage = 1; // Reset to first page when filtering
        this.fetchTraceLogs();
      }, 500);
    },
    formatDateTime(dateTime) {
      return new Date(dateTime).toLocaleString();
    },

    // Truncate log text to show a preview
    truncateLog(log) {
      if (!log) return '';

      const logStr = String(log);
      if (logStr.length <= this.logMaxLength) {
        return logStr;
      }

      return logStr.substring(0, this.logMaxLength) + '...';
    },

    // Check if a log is long enough to be truncated
    isLongLog(log) {
      return log && String(log).length > this.logMaxLength;
    },

    // Show the full log in a modal
    showFullLog(log) {
      this.selectedLog = log;
      this.showLogModal = true;
    },

    // Derive a log level (INFO/WARN/ERROR/DEBUG) from the log line
    logLevel(log) {
      const text = String(log && log.Data ? log.Data : '').toUpperCase();
      if (/\bERROR\b|\bERR\b|\bFATAL\b/.test(text)) return 'ERROR';
      if (/\bWARN(ING)?\b/.test(text)) return 'WARN';
      if (/\bDEBUG\b|\bTRACE\b/.test(text)) return 'DEBUG';
      return 'INFO';
    },

    // Map a level to a drawer/pill tone
    levelTone(level) {
      switch (level) {
        case 'ERROR': return 'err';
        case 'WARN': return 'warn';
        default: return 'neu'; // INFO / DEBUG
      }
    },

    // Drawer handlers
    openRow(log) {
      this.selected = log;
      this.drawerOpen = true;
    },
    closeDrawer() {
      this.drawerOpen = false;
    },
    drawerAction(type) {
      const log = this.selected;
      this.closeDrawer();
      if (!log) return;
      if (type === 'full') {
        this.showFullLog(log.Data);
      } else if (type === 'copy') {
        const line = String(log.Data == null ? '' : log.Data);
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(line);
        }
      }
    },
  },
  computed: {
    // Check if any filters are active
    hasActiveFilters() {
      return this.filters.user ||
             this.filters.service ||
             this.filters.startDate ||
             this.filters.endDate ||
             this.filters.search;
    },
    // Count active filters
    activeFilterCount() {
      let count = 0;
      if (this.filters.user) count++;
      if (this.filters.service) count++;
      if (this.filters.startDate) count++;
      if (this.filters.endDate) count++;
      if (this.filters.search) count++;
      return count;
    },
    // Page-local counts by level (no new API calls)
    errorCount() {
      return this.traceLogs.filter(l => this.logLevel(l) === 'ERROR').length;
    },
    warningCount() {
      return this.traceLogs.filter(l => this.logLevel(l) === 'WARN').length;
    },
    // Apply the rail level view filter (page-local)
    viewFilteredLogs() {
      if (this.levelView === 'errors') {
        return this.traceLogs.filter(l => this.logLevel(l) === 'ERROR');
      }
      if (this.levelView === 'warnings') {
        return this.traceLogs.filter(l => this.logLevel(l) === 'WARN');
      }
      return this.traceLogs;
    },
    drawerFields() {
      const l = this.selected;
      if (!l) return [];
      return [
        {label: 'Time', value: this.formatDateTime(l.Timestamp), mono: true},
        {label: 'Level', value: this.logLevel(l)},
        {label: 'Source', value: l.Service || l.AgentCode || '—'},
        {label: 'Message', value: l.Data},
      ];
    },
  },
  watch: {
    currentPage: "fetchTraceLogs",
    sortBy: "fetchTraceLogs",
    sortDesc: "fetchTraceLogs",
  },
  mounted() {
    this.fetchTraceLogs();
  },
};
</script>

<style scoped>
/* Filters block */
.fm-filters {
  border: 1px solid var(--fm-border);
  border-radius: var(--fm-r-card);
  padding: 16px;
  background: var(--fm-surface-muted);
}
.fm-filter-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--fm-slate-600);
  margin-bottom: 6px;
}

/* Full log modal */
.full-log-content {
  background-color: #1E293B;
  border-radius: 6px;
  padding: 16px;
  max-height: 500px;
  overflow-y: auto;
}

.log-text {
  font-family: var(--fm-font-mono);
  font-size: 0.9em;
  color: #E2E8F0;
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
}
</style>
